import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import useSWR from 'swr';
import { dataFetcher, endpoints } from 'Api';
import { Page } from 'Components/page';
import { AppContext } from 'Context';
import { Pathname } from 'Routes';
import {
  GridShimmer, ViewerGrid, ViewerThemeProvider, ViewerAurora,
} from 'Components/viewer-inner';
import PermissionDeniedModal from 'Components/PermissionDeniedModal';
import { useProjectorBg } from 'hooks/useProfileBg';
import { isNewUIEnabled } from 'hooks/useNewUI';
import { WatchlistClassic } from './WatchlistClassic';
import styles from './index.module.css';

const WatchlistNew = () => {
    // eslint-disable-next-line no-script-url
    window.location.href = 'javascript: $zopim.livechat.button.hide()';
  const { setAppSnackbar, userDetails, langText } = useContext(AppContext);
  const { profileID } = useParams();
  const projectorBg = useProjectorBg(profileID);
  const watchText = langText?.viewProjector;
  const [profileName, setProfileName] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [videos, setVideos] = useState([]);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  useEffect(() => {
    if (userDetails?.firstname) {
      setProfileName(userDetails?.firstname?.substring(0, 10));
    } else if (userDetails?.email) {
      setProfileName(userDetails?.email?.split('@')[0]?.substring(0, 10));
    }
  }, [userDetails]);

  const { mutate: getMyVideos } = useSWR([endpoints.getMyWatchList, profileID || userDetails.myId], {
    fetcher: (url, user_id) => dataFetcher(url, { user_id }),
    onSuccess: ({ success, videos: vids }) => {
      if (success) setVideos(Array.isArray(vids) ? vids : []);
      else setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' });
    },
  });

  useEffect(() => { getMyVideos(); }, []);

  const removeFromWatchlist = async (video) => {
    // Optimistic remove, then reconcile on failure.
    setVideos((prev) => prev.filter((v) => v.id !== video.id));
    try {
      const res = await dataFetcher(endpoints.updateWatchList, { video_id: video.id, action: 'delete' });
      if (res?.success) {
        setAppSnackbar({ isVisible: true, type: 'success', message: 'Removed from watchlist' });
      } else {
        setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not remove from watchlist' });
        getMyVideos();
      }
    } catch (e) {
      setAppSnackbar({ isVisible: true, type: 'error', message: 'Could not remove from watchlist' });
      getMyVideos();
    }
  };

  return (
    <Page noBottomBar noFixedSidebar noDefaultBg>
      <ViewerThemeProvider>
      <ViewerAurora color={projectorBg} />
      <Box sx={{ position: 'relative', zIndex: 1, minHeight: '100vh' }}>
      <Box sx={{ width: '86%', mx: 'auto', px: { xs: '12px', md: '20px' }, py: 3, pb: 6 }}>
        {loading ? (
          <Box sx={{ mx: { xs: '-10px', md: '-15px' } }}><GridShimmer /></Box>
        ) : videos.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#fff', mb: 1 }}>
              Your watchlist is empty
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              Add videos to your watchlist and they’ll show up here.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 20, mb: 2 }}>
              {watchText?.watchListTitle || 'Your Watchlist'}
              <Box component="span" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 500, fontSize: 15, ml: 1 }}>
                {`${videos.length} ${videos.length === 1 ? 'video' : 'videos'}`}
              </Box>
            </Typography>
            <Box sx={{ mx: { xs: '-10px', md: '-15px' } }}>
              <ViewerGrid
                videos={videos}
                onRemove={removeFromWatchlist}
                onLockedClick={() => setShowPermissionModal(true)}
              />
            </Box>
          </>
        )}
      </Box>
      </Box>
      <PermissionDeniedModal
        open={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
      />
      </ViewerThemeProvider>
    </Page>
  );
};

export const Watchlist = (props) => (
  isNewUIEnabled() ? <WatchlistNew {...props} /> : <WatchlistClassic {...props} />
);
